import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Sun, Battery, Flame, Zap, TrendingUp, Leaf, Home } from 'lucide-react';
import { sendButtonClickEvent, sendScrollToTopEvent } from '../../utils/postMessageBridge';
import ModalContactForm from '../forms/ModalContactForm';


const EnergySystemSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('energy-system');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const openFormModal = () => {
    sendButtonClickEvent('ENERGY_SYSTEM_BERATUNGSTERMIN', {
      section: 'energy-system',
      action: 'open-modal'
    });
    
    // Mobile: Sofort öffnen
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      setFormModalOpen(true);
    } else {
      // Desktop: Scroll nach oben, dann Modal öffnen
      sendScrollToTopEvent();
      try {
        window.parent.postMessage({ type: 'SCROLL_TO_TOP' }, '*');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        console.log('PostMessage failed:', e);
      }
      
      setTimeout(() => {
        setFormModalOpen(true);
      }, 400);
    }
  };

  return (
    <section id="energy-system" className="py-20 bg-gradient-to-br from-blue-50 via-white to-green-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-96 h-96 rounded-full" 
             style={{backgroundColor: '#91d33d', filter: 'blur(60px)'}}></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full" 
             style={{backgroundColor: '#57c558', filter: 'blur(60px)'}}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 relative z-50">
            Werden Sie Ihr eigener
            <span className="relative z-50">
              <span className="bg-gradient-to-r from-brand-green to-brand-green-light bg-clip-text text-transparent"> Energieversorger</span>
            </span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Ob als Einzel- oder Systemlösung – mit Photovoltaikanlage, Stromspeicher, Wärmepumpe und Ladesäule können Sie sich selbst mit erneuerbarer Energie versorgen. Das macht unabhängiger von steigenden Energiepreisen, schont den Geldbeutel und schützt die Umwelt.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {[
            {
              icon: Sun,
              title: "Photovoltaik",
              description: "Mit einer eigenen PV-Anlage erzeugen Sie umweltfreundlichen Solarstrom und senken Ihre Stromrechnung erheblich.",
              gradient: "from-yellow-400 to-orange-500"
            },
            {
              icon: Battery,
              title: "Batteriespeicher",
              description: "Mit einem Stromspeicher speichern Sie Ihren eigenen Solarstrom, damit Sie ihn rund um die Uhr nutzen können.",
              gradient: "from-blue-400 to-blue-600"
            },
            {
              icon: Flame,
              title: "Wärmepumpe",
              description: "Mit einer Luft-Wasser-Wärmepumpe heizen Sie durch die Hinzunahme von Umweltwärme effizient und umweltfreundlich.",
              gradient: "from-green-400 to-green-600"
            },
            {
              icon: Zap,
              title: "Ladestation",
              description: "Mit einer Ladestation laden Sie Ihr E-Auto mit kostenlosem Solarstrom und fahren dadurch günstig und emissionsfrei.",
              gradient: "from-purple-400 to-purple-600"
            }
          ].map((component, index) => (
            <div key={index} className={`transition-all duration-1000 delay-${index * 100} ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
              <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 h-full border-2 border-gray-100 hover:border-brand-green/30">
                <div className={`w-14 h-14 bg-gradient-to-br ${component.gradient} rounded-xl flex items-center justify-center mb-4`}>
                  <component.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {component.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {component.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className={`bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="relative h-64 lg:h-auto">
              <img 
                src="/waermepumpe-berlin-brandenburg.jpg"
                alt="Wärmepumpe Berlin Brandenburg - Energiesystem Integration"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = "/waermepumpe-berlin-brandenburg.jpg";
                }}
              />
            </div>

            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 relative z-50">
                Ihre <span className="relative z-50"><span className="bg-gradient-to-r from-brand-green to-brand-green-light bg-clip-text text-transparent">Komplettlösung</span></span> für erneuerbare Energie
              </h3>
              
              <h4 className="text-lg font-semibold text-brand-green mb-4 uppercase tracking-wide relative z-50">
                Solarstrom und Wärmepumpe clever kombinieren
              </h4>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                Steigende Energiepreise und sinkende Einspeisevergütungen machen es heute besonders sinnvoll, möglichst viel vom eigenen Solarstrom selbst zu nutzen. Mit unserem Solarkomplettpaket – bestehend aus Photovoltaik, Speicher, Energiemanagement, Wärmepumpe und optionaler Wallbox – maximieren Sie Eigenverbrauch und Ersparnis.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  { icon: TrendingUp, text: "Maximaler Eigenverbrauch" },
                  { icon: Leaf, text: "100% erneuerbare Energie" },
                  { icon: Home, text: "Unabhängigkeit von Energiepreisen" }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-brand-green/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-4 h-4 text-brand-green" />
                    </div>
                    <span className="text-gray-700 font-medium">{benefit.text}</span>
                  </div>
                ))}
              </div>

              <p className="text-gray-600 mb-8">
                Ein derart optimiertes Energiesystem macht Sie unabhängig von Strom- und Brennstoffpreisen und trägt entscheidend zur Energiewende bei.
              </p>

              <Button
                onClick={openFormModal}
                size="lg"
                className="bg-gradient-to-r from-brand-green to-brand-green-hover text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                BERATUNGSTERMIN
              </Button>
            </div>
          </div>
        </div>
      </div>
      
          <ModalContactForm 
        isOpen={formModalOpen} 
        onClose={() => setFormModalOpen(false)} 
      />
    </section>
  );
};

export default EnergySystemSection;